import type { Ref } from 'vue'
import { computed, ref } from 'vue'
import type { IUiSearchbarOption } from '@/components/UiSearchbar/UiSearchbar.types'
import { CATEGORY_ALL, type TInvestorCategory } from '@/domains/investor/const/InvestorCategories.const'
import { useInvestorFilterOptions } from '@/domains/investor/composables/useInvestorFilterOptions'
import { FILTERS_DEBOUNCE, SUGGESTIONS_LIMIT } from '@/domains/investor/components/DataView/DataView.const'
import type { IDataViewFilterOptions } from '@/domains/investor/components/DataView/DataView.types'
import { watchDebounced } from '@vueuse/core'
import type { IDataViewFiltersOptions } from '@/domains/investor/components/DataView/DataViewFilters/DataViewFilters.types'

export const useDataViewFilters = (
  category: Ref<TInvestorCategory>,
  year: Ref<number[]>,
  months: Ref<number[]>,
  options: IDataViewFiltersOptions,
) => {
  const enableSearch = options.enableSearch ?? true
  const { locale } = useI18n()
  const { categoryOptions, yearOptions } = useInvestorFilterOptions()
  const allowedSecondCategories = options.categoryScope.allSecondCategories ?? options.categoryScope.allowedSecondCategories

  const filteredCategoryOptions = computed(() => categoryOptions.value.filter(option =>
    allowedSecondCategories.includes(option.value)
    || option.value === CATEGORY_ALL,
  ))

  const searchDraft = ref('')
  const suggestions = ref<IUiSearchbarOption[] | null>(null)

  const searchOptions = computed(() => suggestions.value ?? [])
  const searchEmptyText = computed(() => (suggestions.value ? undefined : ''))

  const monthOptions = computed(() => {
    const formatMonth = new Intl.DateTimeFormat(locale.value, { month: 'long' })

    return Array.from({ length: 12 }, (_, index) => ({
      value: index + 1,
      label: formatMonth.format(new Date(2000, index, 1)),
    }))
  })

  const hasActiveFilters = computed(() => Boolean(
    category.value !== CATEGORY_ALL
    || year.value.length
    || months.value.length
    || (enableSearch && searchDraft.value.trim()),
  ))

  const filterOptions = computed<IDataViewFilterOptions>(() => ({
    categoryOptions: filteredCategoryOptions.value,
    yearOptions,
    monthOptions: monthOptions.value,
    searchOptions: searchOptions.value,
    searchEmptyText: searchEmptyText.value,
  }))

  const fetchSuggestions = options.fetchSuggestions

  if (enableSearch && fetchSuggestions) {
    let suggestionsController: AbortController | undefined

    watchDebounced([searchDraft, category, year, months], async () => {
      const query = searchDraft.value.trim()

      suggestionsController?.abort()

      if (!query) {
        suggestions.value = null
        return
      }

      suggestionsController = new AbortController()
      const { signal } = suggestionsController

      try {
        const fetchedSuggestions = await fetchSuggestions({
          locale: locale.value,
          mainCategory: options.categoryScope.mainCategories,
          secondCategory: category.value === CATEGORY_ALL
            ? allowedSecondCategories
            : category.value,
          year: year.value,
          months: months.value,
          search: query || undefined,
          perPage: SUGGESTIONS_LIMIT,
          signal,
        })

        if (query === searchDraft.value.trim()) {
          suggestions.value = fetchedSuggestions
        }
      }
      catch {
        if (!signal.aborted) suggestions.value = null
      }
    }, { debounce: FILTERS_DEBOUNCE, immediate: true })
  }

  return {
    searchDraft,
    searchOptions,
    searchEmptyText,
    categoryOptions: filteredCategoryOptions,
    yearOptions,
    monthOptions,
    filterOptions,
    hasActiveFilters,
  }
}
